import { supabase, isSupabaseConfigured } from './supabaseClient';
import { promotionsData, jobsData } from '../data';

export const LOCALES = ['en', 'fr', 'sw'];

export const pickLocalized = (value, lang) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  const lng = LOCALES.includes(lang) ? lang : 'fr';
  const order = [lng, 'fr', 'en', 'sw'];
  for (const key of order) {
    const text = value[key];
    if (typeof text === 'string' && text.trim()) return text;
  }
  return '';
};

export const localizeRow = (row, lang) => {
  if (!row) return row;
  const localized = { ...row };
  ['title', 'description'].forEach((field) => {
    if (field in localized) localized[field] = pickLocalized(localized[field], lang);
  });
  return localized;
};

const fetchPublished = async (table, orderColumn, ascending = false) => {
  const base = () => supabase.from(table).select('*').order(orderColumn, { ascending });
  const { data, error } = await base().eq('is_published', true);
  if (!error) return { data: data || [], error: null };
  // The is_published column is missing when the database migration has not
  // been applied yet: fall back to every row instead of showing nothing.
  const fallback = await base();
  if (fallback.error) return { data: [], error: fallback.error };
  return { data: (fallback.data || []).map((row) => ({ ...row, is_published: true })), error: null };
};

export async function fetchPromotions() {
  if (!isSupabaseConfigured) {
    return { data: promotionsData, error: null };
  }
  return fetchPublished('promotions', 'start_date', true);
}

export async function fetchRealisations() {
  if (!isSupabaseConfigured) {
    return { data: [], error: null };
  }
  return fetchPublished('realisations', 'created_at', false);
}

export async function fetchJobs() {
  if (!isSupabaseConfigured) {
    return { data: jobsData, error: null };
  }
  return fetchPublished('jobs', 'created_at', false);
}

export const formatDate = (date, lang) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return String(date);
  const locale = lang === 'fr' ? 'fr-FR' : lang === 'sw' ? 'sw' : 'en-GB';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Supabase serves public files from /storage/v1/object/public/<bucket>/<path>.
// A URL stored without the "public" segment (for example one copied from the
// dashboard) would answer with a 400, so it is corrected before being used.
// File names may also contain characters that are not valid in a URL
// (accents, apostrophes, spaces...), so the path is encoded while keeping any
// already encoded sequence (%C3%A9...) untouched.
const encodeSegment = (segment) => segment
  .split(/(%[0-9A-Fa-f]{2})/)
  .map((part, index) => (index % 2 === 1
    ? part
    : encodeURI(part).replace(/['"<>`^{}|\\]/g, (char) => (
      `%${char.charCodeAt(0).toString(16).toUpperCase()}`
    ))))
  .join('');

const encodeStoragePath = (path) => path.split('/').map(encodeSegment).join('/');

export const normalizeStorageUrl = (url) => {
  if (typeof url !== 'string' || !url.startsWith('http')) return url;

  const queryIndex = url.indexOf('?');
  const base = queryIndex === -1 ? url : url.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : url.slice(queryIndex);

  let normalized = base.replace(
    /\/storage\/v1\/object\/(?!public\/|sign\/|authenticated\/)/,
    '/storage/v1/object/public/'
  );

  const marker = '/storage/v1/object/';
  const markerIndex = normalized.indexOf(marker);
  if (markerIndex !== -1) {
    normalized = normalized.slice(0, markerIndex + marker.length)
      + encodeStoragePath(normalized.slice(markerIndex + marker.length));
  }

  return normalized + query;
};

export const resolveImageUrl = (image, fallbackMap = {}) => {
  if (!image) return null;
  if (typeof image === 'string' && (image.startsWith('http') || image.startsWith('//'))) {
    return normalizeStorageUrl(image);
  }
  return fallbackMap[image] || null;
};
